import { useId } from "react";
import { Activity } from "lucide-react";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent } from "../../ui/card";
import { LolBinTimelineData } from "../interfaces/CommonInterfaces";

export default function LolbinTimeLine({
  timelineData = [],
  loading,
  fromdetails = false,
}: {
  timelineData: LolBinTimelineData[];
  loading: boolean;
  fromdetails?: boolean;
}) {
  const gradientId = `endpoint-trend-${useId().replace(/:/g, "")}`;

  return (
    <Card className="overview-card overview-timeline-card">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Timeline"
        description="LOLBin activity over the selected time range"
        icon={Activity}
      />
      <CardContent className="overview-card-content">
        <div className="overview-timeline h-56">
          {loading ? (
            <div className="w-full h-full rounded-md animate-pulse bg-muted" />
          ) : timelineData.length === 0 ? (
            fromdetails ? (
              <OverviewEmptyState
                icon={Activity}
                message="No LOLBin activity found in this time range."
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                No timeline data found.
              </div>
            )
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={timelineData}
                margin={
                  fromdetails
                    ? { top: 12, right: 12, left: -20, bottom: 0 }
                    : undefined
                }
              >
                {fromdetails && (
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0.22}
                      />
                      <stop
                        offset="100%"
                        stopColor="hsl(var(--primary))"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                )}
                <CartesianGrid
                  strokeDasharray={fromdetails ? "4 6" : "3 3"}
                  vertical={fromdetails ? false : undefined}
                  stroke={fromdetails ? "hsl(var(--border))" : undefined}
                  className="opacity-30"
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: fromdetails ? 11 : 10,
                    ...(fromdetails && {
                      fill: "hsl(var(--muted-foreground))",
                    }),
                  }}
                  interval={timelineData.length <= 0 ? 0 : "preserveEnd"}
                  className="text-muted-foreground"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: fromdetails ? 11 : 10,
                    ...(fromdetails && {
                      fill: "hsl(var(--muted-foreground))",
                    }),
                  }}
                  className="text-muted-foreground"
                />
                <Tooltip
                  wrapperClassName={
                    fromdetails ? "overview-chart-tooltip" : undefined
                  }
                  labelFormatter={(label) => `Date: ${label}`}
                  formatter={(value) => [value, "Activity"]}
                />
                <Area
                  type="monotone"
                  dataKey="activity"
                  stroke="hsl(var(--primary))"
                  fill={
                    fromdetails
                      ? `url(#${gradientId})`
                      : "hsl(var(--primary) / 0.2)"
                  }
                  strokeWidth={fromdetails ? 2.5 : 2}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
